import type { UnknownOutputParams } from "@/crossPlatform/expo-router";
import { useLocalSearchParams } from "@/crossPlatform/expo-router";
import { decodeNavigationParam } from "@/utils/navigation.util";

/**
 * Custom hook that automatically decodes navigation parameters from useLocalSearchParams.
 * This prevents RegExp parsing errors when parameters contain special characters like parentheses.
 *
 * @template T - The type of the search params object
 * @returns An object with decoded parameter values
 *
 * @example
 * ```tsx
 * // Instead of:
 * const { channelDisplayName, channelUrl } = useLocalSearchParams<{
 *   channelDisplayName: string;
 *   channelUrl: string;
 * }>();
 *
 * // Use:
 * const { channelDisplayName, channelUrl } = useEncodedSearchParams<{
 *   channelDisplayName: string;
 *   channelUrl: string;
 * }>();
 * ```
 */
const useEncodedSearchParams = <T extends UnknownOutputParams>(): T => {
  const rawParams = useLocalSearchParams<T>();

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
  const decodedParams = {} as T;

  for (const [key, value] of Object.entries(rawParams)) {
    if (typeof value === "string") {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
      (decodedParams as Record<string, unknown>)[key] =
        decodeNavigationParam(value);
    } else if (Array.isArray(value)) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
      (decodedParams as Record<string, unknown>)[key] = value.map((item) =>
        typeof item === "string" ? decodeNavigationParam(item) : item,
      );
    } else {
      // Keep undefined values as-is
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
      (decodedParams as Record<string, unknown>)[key] = value;
    }
  }

  return decodedParams;
};

export default useEncodedSearchParams;
